/**
 * Tour guiado: spotlight + tooltip. TourManager controla el paso.
 */
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ChevronLeft, ChevronRight, CheckCircle, Loader2, X } from 'lucide-react'

const TW = 360
const GAP = 20
const PAD = 16
const SP = 10

function getEl(id) {
  return id ? document.getElementById(id) : null
}

function calcGeometry(el, position, ttH = 200) {
  const vw = window.innerWidth
  const vh = window.innerHeight
  if (!el) {
    return {
      hasSpot: false,
      ttTop: Math.max(PAD, vh / 2 - ttH / 2),
      ttLeft: Math.max(PAD, vw / 2 - TW / 2),
      arrowSide: null,
    }
  }
  const r = el.getBoundingClientRect()
  const spotTop = Math.max(0, r.top - SP)
  const spotLeft = Math.max(0, r.left - SP)
  const spotW = r.width + SP * 2
  const spotH = r.height + SP * 2
  const spaceBottom = vh - (r.bottom + SP)
  const spaceTop = r.top - SP
  const spaceRight = vw - (r.right + SP)
  const spaceLeft = r.left - SP
  const preferred = position || 'bottom'
  let side = preferred
  if (side === 'bottom' && spaceBottom < ttH + GAP) {
    side = spaceTop >= ttH + GAP ? 'top' : spaceRight >= TW + GAP ? 'right' : 'left'
  }
  if (side === 'top' && spaceTop < ttH + GAP) {
    side = spaceBottom >= ttH + GAP ? 'bottom' : spaceRight >= TW + GAP ? 'right' : 'left'
  }
  if (side === 'right' && spaceRight < TW + GAP) {
    side = spaceLeft >= TW + GAP ? 'left' : spaceBottom >= ttH + GAP ? 'bottom' : 'top'
  }
  if (side === 'left' && spaceLeft < TW + GAP) {
    side = spaceRight >= TW + GAP ? 'right' : spaceBottom >= ttH + GAP ? 'bottom' : 'top'
  }
  let ttTop
  let ttLeft
  if (side === 'bottom') {
    ttTop = r.bottom + SP + GAP
    ttLeft = r.left + r.width / 2 - TW / 2
  } else if (side === 'top') {
    ttTop = r.top - SP - GAP - ttH
    ttLeft = r.left + r.width / 2 - TW / 2
  } else if (side === 'right') {
    ttTop = r.top + r.height / 2 - ttH / 2
    ttLeft = r.right + SP + GAP
  } else {
    ttTop = r.top + r.height / 2 - ttH / 2
    ttLeft = r.left - SP - GAP - TW
  }
  ttLeft = Math.max(PAD, Math.min(ttLeft, vw - TW - PAD))
  ttTop = Math.max(PAD, Math.min(ttTop, vh - ttH - PAD))
  return { hasSpot: true, spotTop, spotLeft, spotW, spotH, ttTop, ttLeft, arrowSide: side }
}

function Arrow({ side }) {
  if (!side) return null
  const S = 9
  const base = { position: 'absolute', width: 0, height: 0, border: `${S}px solid transparent` }
  const styles = {
    bottom: { ...base, borderBottomColor: 'white', top: -S * 2, left: '50%', transform: 'translateX(-50%)' },
    top: { ...base, borderTopColor: 'white', bottom: -S * 2, left: '50%', transform: 'translateX(-50%)' },
    right: { ...base, borderRightColor: 'white', top: '50%', left: -S * 2, transform: 'translateY(-50%)' },
    left: { ...base, borderLeftColor: 'white', top: '50%', right: -S * 2, transform: 'translateY(-50%)' },
  }
  return <div aria-hidden style={styles[side]} />
}

const FeatureTour = ({
  isOpen,
  steps = [],
  stepIndex = 0,
  onNext,
  onPrev,
  onClose,
  label = 'Tour guiado',
  navigating = false,
  informativo = false,
}) => {
  const [tick, setTick] = useState(0)
  const [visible, setVisible] = useState(false)
  const tooltipRef = useRef(null)
  const mounted = useRef(false)

  const total = steps.length
  const safeIndex = total > 0 ? Math.min(Math.max(0, stepIndex), total - 1) : 0
  const currentStep = steps[safeIndex]
  const isLast = total > 0 && safeIndex >= total - 1
  const progress = total > 0 ? ((safeIndex + 1) / total) * 100 : 0
  const accentColor = currentStep?.accentColor || '#2563eb'

  const el = informativo ? null : getEl(currentStep?.targetId)
  const ttH = tooltipRef.current?.offsetHeight || 200
  const geo = calcGeometry(el, currentStep?.position, ttH)

  useLayoutEffect(() => {
    if (!isOpen || !currentStep) {
      setVisible(false)
      return
    }
    mounted.current = true
    setVisible(true)
    const target = currentStep.targetId ? document.getElementById(currentStep.targetId) : null
    if (target) {
      try {
        target.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'nearest' })
      } catch {
        /* ignore */
      }
    }
    if (mounted.current) setTick((t) => t + 1)
    const onLayout = () => {
      if (mounted.current) setTick((t) => t + 1)
    }
    window.addEventListener('resize', onLayout)
    window.addEventListener('scroll', onLayout, true)
    return () => {
      window.removeEventListener('resize', onLayout)
      window.removeEventListener('scroll', onLayout, true)
    }
  }, [isOpen, stepIndex, currentStep?.targetId])

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  if (!isOpen || steps.length === 0) return null

  const content = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${label} — paso ${safeIndex + 1} de ${total}`}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99997,
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.2s ease',
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 99996,
          pointerEvents: 'auto',
          background: 'transparent',
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      />
      <style>{`
        @keyframes ft-pulse {
          0%, 100% { box-shadow: 0 0 0 3px #3b82f6, 0 0 18px rgba(59,130,246,0.40); }
          50% { box-shadow: 0 0 0 6px #3b82f6, 0 0 30px rgba(59,130,246,0.65); }
        }
        @keyframes ft-slide-in {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes ft-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {geo.hasSpot ? (
        <svg
          aria-hidden
          style={{
            position: 'fixed',
            inset: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 99998,
          }}
        >
          <defs>
            <mask id="ft-mask-tour">
              <rect width="100%" height="100%" fill="white" />
              <rect x={geo.spotLeft} y={geo.spotTop} width={geo.spotW} height={geo.spotH} rx={10} fill="black" />
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(0,0,0,0.72)" mask="url(#ft-mask-tour)" />
        </svg>
      ) : (
        <div
          aria-hidden
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.72)',
            pointerEvents: 'none',
            zIndex: 99998,
          }}
        />
      )}

      {geo.hasSpot && !navigating && (
        <div
          aria-hidden
          style={{
            position: 'fixed',
            top: geo.spotTop,
            left: geo.spotLeft,
            width: geo.spotW,
            height: geo.spotH,
            borderRadius: 11,
            pointerEvents: 'none',
            zIndex: 99999,
            animation: 'ft-pulse 2s ease-in-out infinite',
          }}
        />
      )}

      <div
        ref={tooltipRef}
        style={{
          position: 'fixed',
          top: geo.ttTop,
          left: geo.ttLeft,
          width: TW,
          background: 'white',
          borderRadius: 14,
          padding: '18px 20px 14px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.28), 0 0 0 1px rgba(0,0,0,0.06)',
          zIndex: 100000,
          animation: visible ? 'ft-slide-in 0.25s ease' : 'none',
        }}
      >
        <Arrow side={geo.arrowSide} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: '0.66rem', fontWeight: 600, color: '#9ca3af', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            {safeIndex + 1} / {total}
          </span>
          <button
            type="button"
            onClick={onClose}
            title="Cerrar"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#9ca3af',
              display: 'flex',
              padding: 2,
              borderRadius: 4,
            }}
          >
            <X size={15} />
          </button>
        </div>
        <div style={{ width: '100%', height: 3, background: '#f0f0f0', borderRadius: 99, marginBottom: 13 }}>
          <div
            style={{
              height: 3,
              borderRadius: 99,
              background: accentColor,
              width: `${progress}%`,
              transition: 'width 0.35s ease',
            }}
          />
        </div>
        {navigating && !informativo ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '10px 0 6px' }}>
            <Loader2 size={26} style={{ animation: 'ft-spin 0.8s linear infinite', color: accentColor }} />
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280', textAlign: 'center' }}>Navegando…</p>
          </div>
        ) : (
          <>
            <h3 style={{ margin: '0 0 6px', fontSize: '0.975rem', fontWeight: 700, color: '#111827', lineHeight: 1.35 }}>
              {currentStep?.title}
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: '0.845rem', color: '#6b7280', lineHeight: 1.65 }}>{currentStep?.description}</p>
          </>
        )}
        {!navigating && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.76rem', color: '#b0b8c4', padding: '4px 0' }}>
              Saltar
            </button>
            <div style={{ display: 'flex', gap: 7 }}>
              {safeIndex > 0 && (
                <button
                  type="button"
                  onClick={onPrev}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                    padding: '6px 11px',
                    borderRadius: 7,
                    fontSize: '0.8rem',
                    border: '1px solid #e5e7eb',
                    background: 'white',
                    color: '#374151',
                    cursor: 'pointer',
                    fontWeight: 500,
                  }}
                >
                  <ChevronLeft size={13} /> Anterior
                </button>
              )}
              <button
                type="button"
                onClick={isLast ? onClose : onNext}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '6px 13px',
                  borderRadius: 7,
                  fontSize: '0.8rem',
                  background: isLast ? '#16a34a' : accentColor,
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                  boxShadow: `0 2px 8px ${(isLast ? '#16a34a' : accentColor)}55`,
                }}
              >
                {isLast ? (
                  <>
                    <CheckCircle size={13} /> Finalizar
                  </>
                ) : (
                  <>
                    Siguiente <ChevronRight size={13} />
                  </>
                )}
              </button>
            </div>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 5, marginTop: 13 }}>
          {steps.map((_, i) => (
            <div
              key={i}
              style={{
                width: i === safeIndex ? 16 : 6,
                height: 6,
                borderRadius: 99,
                background: i === safeIndex ? accentColor : '#e5e7eb',
                transition: 'all 0.22s ease',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  )

  return createPortal(content, document.body)
}

export default FeatureTour
